import React, { useState } from 'react';

import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import PropTypes from 'prop-types';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import ModalBox from 'components/application/ModalBox';

import SortableDocumentRow from 'components/projectDocuments/SortableDocumentsRow';

import ProgressIcon from '-!svg-react-loader?name=LoadingIcon!icons/progress.svg';

export default function ReorderDocumentsModal({
  isUpdatePdfPositionMutationLoading,
  onClose,
  onSave,
  projectPdfs,
}) {
  const [orderedProjectPdfIds, setOrderedProjectPdfIds] = useState(
    projectPdfs.map((pdf) => pdf.id),
  );
  const [draggedRowProjectPdfId, setDraggedRowProjectPdfId] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = orderedProjectPdfIds.indexOf(active.id);
      const newIndex = orderedProjectPdfIds.indexOf(over.id);
      setOrderedProjectPdfIds(
        arrayMove(orderedProjectPdfIds, oldIndex, newIndex),
      );
    }
    setDraggedRowProjectPdfId(null);
  };

  const isSorting = !!draggedRowProjectPdfId;

  return (
    <ModalBox
      customFooter={
        <div className='modalbox__footer-actions modalbox__footer-actions--right'>
          <TextButton
            className='tw-mr-3'
            color='blue'
            disabled={isUpdatePdfPositionMutationLoading}
            onClick={onClose}
            size='md'
          >
            Cancel
          </TextButton>
          <FilledButton
            color='blue'
            disabled={isUpdatePdfPositionMutationLoading}
            onClick={() => onSave(orderedProjectPdfIds)}
            size='md'
          >
            <ProgressIcon
              className={`${isUpdatePdfPositionMutationLoading ? 'tw-mr-1' : 'tw-hidden'} [&_path]:tw-fill-white`}
              height={18}
              width={18}
            />
            Save changes
          </FilledButton>
        </div>
      }
      isOpen={true}
      modalBoxClassName='tw-w-160'
      mode='flexible'
      onClose={onClose}
    >
      <div className='tw-p-6'>
        <h2 className='tw-m-0 tw-p-0 tw-text-xl tw-font-semibold tw-text-grey-900'>
          Reorder additional documents
        </h2>
        <div className='tw-mt-6 tw-rounded-lg tw-border-1 tw-border-b-0 tw-border-solid tw-border-grey-100 tw-bg-white'>
          <div className='tw-max-h-[370px] tw-overflow-y-auto'>
            <div
              className='tw-rounded-bl-[3px] tw-rounded-br-[3px] tw-border-grey-100'
              role='table'
            >
              <div
                className='tw-sticky tw-top-0 tw-z-10 tw-h-7.5'
                role='rowgroup'
              >
                <div className='tw-flex tw-h-7.5 tw-bg-grey-050' role='row'>
                  <div className='tw-w-10' role='columnheader' />
                  <div
                    className='tw-flex tw-w-108 tw-items-center tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'
                    role='columnheader'
                  >
                    Document
                  </div>
                  <div
                    className='tw-flex tw-w-30 tw-items-center tw-justify-center tw-text-center tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'
                    role='columnheader'
                  >
                    Cover sheet
                  </div>
                </div>
              </div>
              <div className='tw-relative' role='rowgroup'>
                <DndContext
                  collisionDetection={closestCenter}
                  modifiers={[restrictToParentElement, restrictToVerticalAxis]}
                  onDragEnd={handleDragEnd}
                  onDragStart={(event) => {
                    setDraggedRowProjectPdfId(event.active.id);
                  }}
                  sensors={sensors}
                >
                  <SortableContext
                    items={orderedProjectPdfIds}
                    strategy={verticalListSortingStrategy}
                  >
                    {orderedProjectPdfIds.map((pdfId) => {
                      const pdf = projectPdfs.find((pdf) => pdf.id === pdfId);
                      return (
                        <SortableDocumentRow
                          isSorting={isSorting}
                          key={pdf.id}
                          pdf={pdf}
                        />
                      );
                    })}
                  </SortableContext>
                </DndContext>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalBox>
  );
}

ReorderDocumentsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  projectPdfs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      attributes: PropTypes.shape({
        filename: PropTypes.string.isRequired,
        coverSheet: PropTypes.bool.isRequired,
      }).isRequired,
    }),
  ).isRequired,
  isUpdatePdfPositionMutationLoading: PropTypes.bool,
};
